import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { CarForm, carValidation } from '@zydalabs/storefront-components';
import { Button } from '@zydalabs/zac-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useUpdateCar, useAddCar, useShowCar } from 'service/hooks';
import { StoreFrontHeader } from 'common/components';
import { StyledPageWrapper, StyledStickySection, StyledFormWrapper } from 'common/styles';
import { URLS } from 'common/constants';
import { localizeErrorMessages } from 'modules/userModule/utils';
import { usePersistedUserCarId } from 'common/hooks';
import { replaceRoute } from 'common/utils';
import useTranslation from 'next-translate/useTranslation';
import CarLoading from './CarLoading';

const getFieldsLocalized = t => ({
  brand: t('brand'),
  color: t('color'),
  license: `${t('license')} ${t('optional')}`,
});

const CarPage: React.FC = () => {
  const router = useRouter();
  const carId = router.query.id as string;
  const { t } = useTranslation('car');

  const [, setPersistedUserCarId] = usePersistedUserCarId();
  const addCar = useAddCar();
  const updateCar = useUpdateCar({ carId });
  const { data: showCarData, isLoading: isCarLoading } = useShowCar({ carId });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm({
    resolver: yupResolver(carValidation),
    defaultValues: showCarData,
  });

  useEffect(() => {
    if (showCarData) {
      const formattedShowCarData = {
        ...showCarData,
        brand: showCarData.make,
        license: showCarData.licenseNumber,
      };
      reset(formattedShowCarData);
    }
  }, [showCarData, reset]);

  const handleOnSubmit = data => {
    const carMutationObject = {
      make: data.brand,
      color: data.color,
      licenseNumber: data.license,
    };

    const mutateCar = carId ? updateCar : addCar;

    mutateCar({
      ...carMutationObject,
    })
      .then(car => {
        if (car?.id) setPersistedUserCarId(car.id);
        replaceRoute(URLS.CHECKOUT);
      })
      .catch(
        ({ errors: err }) =>
          err &&
          setError(err.attribute, {
            type: 'validation',
            message: err.message,
          }),
      );
  };

  if (isCarLoading) return <CarLoading />;

  const fields = getFieldsLocalized(t);

  return (
    <StyledPageWrapper>
      <StoreFrontHeader title={t('newCar')} />
      {/* TODO: format errors before passing it to the form */}
      <StyledFormWrapper>
        <CarForm fields={fields} errors={localizeErrorMessages(fields, errors, t)} control={control} autoFocus />
      </StyledFormWrapper>
      <StyledStickySection>
        <Button type="submit" isFluid onClick={handleSubmit(handleOnSubmit)} text={t('save')} testId="save_car_btn" />
      </StyledStickySection>
    </StyledPageWrapper>
  );
};

export default CarPage;
